import { QueryClient, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CachedQueryKeys } from './cachedQueryKeys';
import {
  getActiveCarts,
  AllUserRetailerShoppingCartsResponse,
  updateItemsToUserRetailerShoppingCart,
  removeItemFromShoppingCart,
} from '../rest/checkout';
import { sumAllItemQuantities, sumItemQuantities } from '@/utils';
import { BCQueryOptions } from './types';
import BagButton from '@/components/button/BagButton.tsx';

import _cloneDeep from 'lodash/cloneDeep';
import { CartRefusal, validateCartItemLimits } from '@/utils/cartValidation';
import { authReady } from '@/utils/authReady';
import { alignProductsToCartSkus, ShoppingCartItem, UserRetailerShoppingCart } from '@brandclub/common-ui';

/**
 * Updates the cart data **in-place**
 *
 * @param carts - The array of carts to update
 * @param retailerId - The retailer ID of the cart to update
 * @param items - The items to update the cart with
 * @param action - The action to perform on the cart
 */
const mutateLocalCarts = (
  carts: UserRetailerShoppingCart[] = [],
  retailerId: number,
  items: ShoppingCartItem[],
  action: 'add' | 'remove',
) => {
  // Find the cart for this retailer
  const cartIndex = carts.findIndex((cart) => cart.retailerId === retailerId);

  // If the cart is NOT found and we're NOT adding a new one, there's nothing to do
  if (cartIndex === -1 && action !== 'add') {
    return;
  }
  // Handle the case where we're adding to a non-existent cart
  if (cartIndex === -1) {
    const userId = carts[0]?.userId || '';
    // create a new cart with the items
    carts.push({
      retailerId,
      items: [...items], // Clone the items array
      totalItems: sumItemQuantities(items),
      createdTime: new Date(),
      updatedTime: new Date(),
      userId,
      shoppingCartId: '',
      createdBy: '',
      updatedBy: '',
      sessionStatus: 'active',
      estimatedTax: 0,
      estimatedShippingFees: 0,
    });
    // return early because we don't need to update the cart
    return;
  }

  // Update the existing cart
  const cart = carts[cartIndex];
  const updatedItems = updateCartItems(cart.items, items, action);

  // If all items were removed, remove the cart entirely
  if (updatedItems.length === 0) {
    carts.splice(cartIndex, 1);
    return;
  }

  // Update the cart with new items and recalculate totals
  cart.items = updatedItems;
};

/**
 * Updates cart items based on the action
 * Returns a new array of items
 */
const updateCartItems = (
  existingItems: ShoppingCartItem[] = [],
  newItems: ShoppingCartItem[],
  action: 'add' | 'remove',
): ShoppingCartItem[] => {
  if (action === 'remove') {
    return existingItems.filter((item) => !newItems.some((newItem) => newItem.retailerSku === item.retailerSku));
  }
  // Create a map of existing items for easier updates
  const itemMap = new Map();
  existingItems.forEach((item) => itemMap.set(item.retailerSku, { ...item }));

  // Process each new item
  for (const newItem of newItems) {
    const existingItem = itemMap.get(newItem.retailerSku);

    if (existingItem) {
      // Update existing item
      existingItem.quantity = Math.max(0, existingItem.quantity + newItem.quantity); // Remove item if quantity becomes zero
      if (existingItem.quantity === 0) {
        itemMap.delete(newItem.retailerSku);
        continue;
      }
    } else if (action === 'add') {
      // Add new item (only for 'add' action)
      itemMap.set(newItem.retailerSku, { ...newItem });
    }
  }

  // Convert the map back to an array
  return Array.from(itemMap.values());
};

/**
 * The limits judge the cart a change leaves: each line it raises, at its new quantity, and the new total. Another
 * line already over a limit does not block the change, and a change that lowers a line is never refused.
 * Throws `CartRefusal`.
 */
const assertWithinLimits = (carts: UserRetailerShoppingCart[], retailerId: number, items: ShoppingCartItem[]) => {
  const totalItems = sumAllItemQuantities(carts);
  const raisedSkus = new Set(items.filter((item) => item.quantity > 0).map((item) => item.retailerSku));
  (carts.find((cart) => cart.retailerId === retailerId)?.items ?? [])
    .filter((item) => raisedSkus.has(item.retailerSku))
    .forEach((item) => validateCartItemLimits(item.quantity, totalItems));
};

// Define a type for cart operation parameters
interface CartOperationParams {
  action: 'add' | 'remove';
  retailerId: number;
  items: ShoppingCartItem[];
}

/** True when a cart the server returned is the same cart as one in the cache. */
const isSameCart = (cachedCart: UserRetailerShoppingCart, serverCart: UserRetailerShoppingCart) => {
  if (serverCart.shoppingCartId && cachedCart.shoppingCartId === serverCart.shoppingCartId) {
    return true;
  }
  if (cachedCart.retailerId === serverCart.retailerId && cachedCart.userId === serverCart.userId) {
    return true;
  }
  // The first-add optimistic cart has no shoppingCartId and a placeholder userId. Match it on retailerId alone,
  // so the server cart replaces it and does not duplicate it.
  return cachedCart.retailerId === serverCart.retailerId && !cachedCart.shoppingCartId;
};

const upsertCartInCache = (
  cachedData: AllUserRetailerShoppingCartsResponse,
  updatedCart: UserRetailerShoppingCart,
): AllUserRetailerShoppingCartsResponse => {
  const existingIndex = cachedData.userRetailerShoppingCarts.findIndex((cart) => isSameCart(cart, updatedCart));

  if (existingIndex === -1) {
    return {
      ...cachedData,
      userRetailerShoppingCarts: [...cachedData.userRetailerShoppingCarts, updatedCart],
    };
  }

  const nextCarts = [...cachedData.userRetailerShoppingCarts];
  nextCarts[existingIndex] = {
    ...nextCarts[existingIndex],
    ...updatedCart,
  };

  return {
    ...cachedData,
    userRetailerShoppingCarts: nextCarts,
  };
};

const handleCartOperation = async (params: CartOperationParams, queryClient: QueryClient) => {
  const { action, retailerId, items } = params;
  if (action === 'remove') {
    const response = await removeItemFromShoppingCart({
      retailerId,
      items: items,
    });
    return response;
  }
  if (action !== 'add') {
    throw new Error('Invalid action');
  }

  const optimisticallyUpdatedCarts = queryClient.getQueryData(activeCartsQueryOptions().queryKey);
  const updatedRetailerCart = optimisticallyUpdatedCarts?.userRetailerShoppingCarts?.find(
    (cart) => cart.retailerId === retailerId,
  );

  if (!optimisticallyUpdatedCarts || !updatedRetailerCart) {
    // The optimistic paint did not run, so the cache holds no line at its new quantity. Send the pressed items.
    BagButton?.AnimateAddProductToBag(items[0].stacklineSku);
    const response = await updateItemsToUserRetailerShoppingCart({
      retailerId,
      items,
    });
    return response;
  }

  const requestedItemsSkuSet = new Set(items.map((item) => item.retailerSku));
  // The optimistic cart holds each requested line at its new quantity. `onMutate` checked the limits before it.
  const updatedItems = updatedRetailerCart.items.filter((item: ShoppingCartItem) =>
    requestedItemsSkuSet.has(item.retailerSku),
  );

  BagButton?.AnimateAddProductToBag(items[0].stacklineSku);
  const response = await updateItemsToUserRetailerShoppingCart({
    retailerId,
    items: updatedItems,
  });
  return response;
};

/**
 * The product records to draw the bag with, found by the stackline sku each
 * line carries.
 *
 * The server stores a record under the sku of the catalog it read, and that
 * sku can differ from the line's. `alignProductsToCartSkus` then matches by
 * retailer sku. Use this as the `select` of every reader of `products`.
 */
export const selectCartProducts = (data: AllUserRetailerShoppingCartsResponse) =>
  alignProductsToCartSkus(data.products, data.userRetailerShoppingCarts);

/** True when some bag line has no product record to draw it with. */
const hasLineWithoutRecord = (data: AllUserRetailerShoppingCartsResponse) => {
  const products = selectCartProducts(data);
  return data.userRetailerShoppingCarts.some((cart) =>
    (cart.items ?? []).some((item) => !!item.stacklineSku && !products[item.stacklineSku]),
  );
};

let latestBagRefresh = 0;
// The server omits `primeDelivery` for an empty cart and after a failed preview, so remember that the store offers it.
let bagOffersPrime = false;
const hasPrimeDelivery = (data: AllUserRetailerShoppingCartsResponse) =>
  data.userRetailerShoppingCarts.some((cart) => cart.primeDelivery);

/**
 * After a save, refresh the product records and each cart's Prime delivery. A save returns only the lines, and a
 * new line has no record. Take only `products` and `primeDelivery` from the fetch, because its lines can be older
 * than the cache. Only the latest refresh writes.
 */
const refreshBagDerivedData = async (queryClient: QueryClient) => {
  const cartQueryKey = activeCartsQueryOptions().queryKey;
  const current = queryClient.getQueryData(cartQueryKey);
  if (!current) return;
  bagOffersPrime ||= hasPrimeDelivery(current);
  if (!bagOffersPrime && !hasLineWithoutRecord(current)) return;
  const refresh = ++latestBagRefresh;
  try {
    const fetched = await getActiveCarts();
    if (refresh !== latestBagRefresh) return;
    bagOffersPrime ||= hasPrimeDelivery(fetched);
    queryClient.setQueryData<AllUserRetailerShoppingCartsResponse>(cartQueryKey, (latest) =>
      latest
        ? {
            ...latest,
            products: { ...latest.products, ...fetched.products },
            userRetailerShoppingCarts: latest.userRetailerShoppingCarts.map((cart) => ({
              ...cart,
              primeDelivery: fetched.userRetailerShoppingCarts.find((fetchedCart) => isSameCart(cart, fetchedCart))
                ?.primeDelivery,
            })),
          }
        : latest,
    );
  } catch (error) {
    // The bag still works without them. The next full fetch fills them.
    console.warn('Could not refresh the bag after a save:', error);
  }
};

export const activeCartsQueryOptions = () => {
  return queryOptions({
    queryKey: [CachedQueryKeys.UserCart],
    queryFn: async ({ signal }) => {
      // Before auth setup there is no token, and the server answers with the visitor bag.
      // Wait, so a signed-in shopper does not see the visitor bag first.
      await authReady;
      return getActiveCarts(signal);
    },
    staleTime: 0,
    refetchOnWindowFocus: 'always',
  });
};

/**
 * The cart key names no shopper, so the cart this browser holds belongs to whoever read it last. A sign-in change,
 * and a page the browser restored from memory, drop it. The mounted cart query then reads it again, and nothing
 * shows the dropped cart while that read runs.
 */
export const dropCart = (queryClient: QueryClient) =>
  queryClient.resetQueries({ queryKey: activeCartsQueryOptions().queryKey });

export const useAllCarts = <TSelect = AllUserRetailerShoppingCartsResponse>(
  options?: BCQueryOptions<typeof activeCartsQueryOptions, AllUserRetailerShoppingCartsResponse, TSelect>,
) => {
  return useQuery({
    ...activeCartsQueryOptions(),
    ...options,
    select: options?.select,
  });
};

export const useCartCount = () => {
  return useQuery({
    ...activeCartsQueryOptions(),
    select: (data) => sumAllItemQuantities(data.userRetailerShoppingCarts),
    // ensures we don't trigger any fetches, only read from cache and re-render when the cache is updated
    enabled: false,
    staleTime: Infinity,
  });
};

/**
 * Hook for optimistically updating shopping cart items
 * Returns a mutation function that can be used to add/remove items from cart
 */
export const useCartUpdates = () => {
  const queryClient = useQueryClient();
  const cartQueryKey = activeCartsQueryOptions().queryKey;

  return useMutation({
    mutationFn: (params: CartOperationParams) => handleCartOperation(params, queryClient),

    onMutate: async ({ retailerId, items, action }: CartOperationParams) => {
      // The write sets each line's quantity, so the change starts from the cart the cache holds. With a cart in the
      // cache, a read in flight is cancelled so it cannot paint over this change. With none, as just after a
      // sign-in, the press waits for the cart read, and a second press waits for the same read.
      if (queryClient.getQueryData(cartQueryKey)) await queryClient.cancelQueries({ queryKey: cartQueryKey });
      else await queryClient.ensureQueryData(activeCartsQueryOptions());
      // Read after the wait, so a press sees the paint of the press before it. It is also what a rollback restores.
      const previousData = queryClient.getQueryData(cartQueryKey);
      if (!previousData) return { previousData: null };

      // Create a deep copy of the carts to avoid mutating the cache directly
      const localCarts = _cloneDeep(previousData?.userRetailerShoppingCarts ?? []);
      try {
        mutateLocalCarts(localCarts, retailerId, items, action);
      } catch (error) {
        console.error('Error in optimistic update:', error);
        return { previousData };
      }
      // A refused change is never painted, so the limits judge the cart before the cache shows it.
      if (action === 'add') assertWithinLimits(localCarts, retailerId, items);

      queryClient.setQueryData<AllUserRetailerShoppingCartsResponse>(cartQueryKey, {
        ...previousData,
        userRetailerShoppingCarts: localCarts,
      });
      return { previousData };
    },

    onSuccess: (response) => {
      if (!response?.userRetailerShoppingCart && !response?.userRetailerShoppingCarts?.length) {
        return;
      }

      queryClient.setQueryData<AllUserRetailerShoppingCartsResponse>(cartQueryKey, (currentData) => {
        // A sign-in change dropped the cart while the write ran. The answer belongs to the cart that was dropped,
        // and the next read paints the current one.
        if (!currentData) return currentData;

        if (response.userRetailerShoppingCarts?.length) {
          return {
            ...currentData,
            status: response.status,
            userRetailerShoppingCarts: response.userRetailerShoppingCarts,
          };
        }

        return upsertCartInCache(currentData, response.userRetailerShoppingCart!);
      });

      // The save response carries no product records and no Prime delivery.
      void refreshBagDerivedData(queryClient);
    },

    onError: async (error, _params, context) => {
      // A refusal comes before any paint or write, so the cart is as it was.
      if (error instanceof CartRefusal) return;
      if (context?.previousData) {
        queryClient.setQueryData(cartQueryKey, context.previousData);
      }
      try {
        await queryClient.fetchQuery(activeCartsQueryOptions());
      } catch {
        // Server fetch failed — rollback data preserved, onSettled marks stale
      }
    },

    onSettled: () => {
      // Mark stale so subsequent reads can refresh if needed.
      queryClient.invalidateQueries({
        queryKey: cartQueryKey,
        refetchType: 'none',
      });
    },
  });
};
